// Shared style constants for the read-only topic view, consumed by both the
// legacy `TopicView` (view-mode branch) and the component-driven `TopicReadPage`
// shell + per-type read bodies. Keeping a single definition here prevents the
// render paths from drifting.

import type React from 'react';
import { pxToRem } from '@/modules/config/constants';

export const pageBackground = {
  '--background': 'var(--app-page-background)',
} as React.CSSProperties;

export const pageHeaderTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: pxToRem(24),
  fontWeight: 600,
  textAlign: 'center',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  userSelect: 'text',
};

// View-mode "hero" layout: on a wide-enough viewport the image and the
// name/type sit side-by-side (each ~50% via flex-basis + wrap); on a narrow
// phone they wrap and stack. This compresses the area above the description so
// more of the description is visible without scrolling.
//
// The flex-basis is the wrap threshold: two columns stay on one row only while
// `2 * basis + gap` fits the container. A typical phone gives ~330–400px of
// inner width after page padding, so keep the basis well under ~160px or the
// columns wrap to stacked on every phone (which is what a 200px basis did).
// The two padded containers of a read body (the legacy `TopicView` view branch
// and each `<type>/ReadOnlyView`): the hero block above the description, and the
// scrollable content below it. Shared so every topic type keeps the same
// read-view edge padding.
//
// The hero's generous bottom padding is load-bearing: the type badge closing the
// hero and a SectionCard title opening the body are both small uppercase grey
// labels, so only the gap tells a reader which one the badge belongs to. Keep it
// well clear of `heroInfoColStyle`'s internal gap.
export const readHeroStyle: React.CSSProperties = { padding: '20px 16px 12px' };
export const readBodyStyle: React.CSSProperties = {
  padding: '8px 16px',
  paddingBottom: 120,
};

export const heroRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '16px',
};
export const heroImageColStyle: React.CSSProperties = {
  flex: '1 1 160px',
  minWidth: 0,
};
export const heroImageStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  maxHeight: '240px',
  objectFit: 'cover',
  borderRadius: '12px',
};
// Holds the title row and the type badge. The gap is deliberately tight: the
// badge is a kicker for the title above it, and has to read that way rather than
// as a heading for the first card below.
export const heroInfoColStyle: React.CSSProperties = {
  flex: '1 1 160px',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};
export const heroTitleRowStyle: React.CSSProperties = {
  position: 'relative',
  minHeight: '44px',
  display: 'flex',
  alignItems: 'center',
  paddingRight: '32px',
};
export const heroTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: pxToRem(24),
  fontWeight: 600,
  textAlign: 'left',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  userSelect: 'text',
};
export const fieldLabelStyle: React.CSSProperties = {
  fontSize: pxToRem(13),
  color: '#000',
  marginBottom: '2px',
};
export const fieldValueStyle: React.CSSProperties = {
  fontSize: pxToRem(16),
  color: 'var(--ion-color-secondary)',
  margin: 0,
  userSelect: 'text',
};

// The overflow menu's hidden probe-guard container: it must render (so the
// ActionGuardContainer can compute "any action allowed") but be invisible.
export const topicGuardRegistrationStyle: React.CSSProperties = {
  display: 'none',
};
